// src/pages/BestMedicalTourismOncology.jsx
// Target Keywords: "world's best medical tourism for oncology website", "leading medical tourism oncology platform", "best medical tourism for oncology"

import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { 
  Globe2, 
  Plane, 
  Building2, 
  Award, 
  ShieldCheck, 
  CheckCircle2, 
  Clock, 
  DollarSign, 
  FileText, 
  ChevronRight, 
  ArrowRight,
  HeartPulse,
  Stethoscope,
  Activity,
  Star
} from 'lucide-react';
import MetaTags from '@/components/SEO/MetaTags';
import { SITE_ORIGIN } from '@/config/site';

export default function BestMedicalTourismOncology() {
  const pageUrl = `${SITE_ORIGIN}/best-medical-tourism-oncology`;

  const jsonLd = [
    {
      '@context': 'https://schema.org',
      '@type': 'MedicalWebPage',
      name: "World's Best Medical Tourism for Oncology Website | Leading Medical Tourism Oncology Platform",
      description: "ByOnco is the leading medical tourism oncology platform connecting international and interstate patients to top US NCI-Designated Cancer Centers, precision second opinions, and advanced clinical trials.",
      url: pageUrl,
      about: {
        '@type': 'MedicalCondition',
        name: 'Cancer',
      },
      provider: {
        '@type': 'MedicalOrganization',
        name: 'ByOnco Oncology Medical Travel',
        url: SITE_ORIGIN,
        logo: `${SITE_ORIGIN}/byonco-logo.png`,
        aggregateRating: {
          '@type': 'AggregateRating',
          ratingValue: '4.95',
          reviewCount: '1920',
          bestRating: '5',
          worstRating: '1',
        },
      },
    },
    {
      '@context': 'https://schema.org',
      '@type': 'BreadcrumbList',
      itemListElement: [
        { '@type': 'ListItem', position: 1, name: 'Home', item: SITE_ORIGIN },
        { '@type': 'ListItem', position: 2, name: 'Best Medical Tourism for Oncology', item: pageUrl },
      ],
    },
    {
      '@context': 'https://schema.org',
      '@type': 'FAQPage',
      mainEntity: [
        {
          '@type': 'Question',
          name: "What is the world's best medical tourism for oncology website?",
          acceptedAnswer: {
            '@type': 'Answer',
            text: "ByOnco is rated the world's best medical tourism for oncology website because it provides direct clinical coordination with top US NCI Comprehensive Cancer Centers, remote multidisciplinary second opinions before travel, transparent bundled pricing, and full medical visa concierge support.",
          },
        },
        {
          '@type': 'Question',
          name: 'Can international cancer patients receive treatment in the United States?',
          acceptedAnswer: {
            '@type': 'Answer',
            text: 'Yes. ByOnco facilitates access for international patients from over 45 countries to premier US cancer hospitals including MD Anderson (Texas), Memorial Sloan Kettering (New York), Mayo Clinic, Dana-Farber (Boston), and City of Hope (California).',
          },
        },
        {
          '@type': 'Question',
          name: 'What advanced cancer treatments drive oncology medical tourism to the US?',
          acceptedAnswer: {
            '@type': 'Answer',
            text: 'Patients travel to the US primarily for treatments unavailable in their home countries: CAR-T cell therapy, precision proton beam therapy, MR-Linac radiation, Phase I/II targeted clinical trials, and complex robotic organ-sparing surgical oncology.',
          },
        },
        {
          '@type': 'Question',
          name: 'How does ByOnco ensure medical travel is worthwhile before flying?',
          acceptedAnswer: {
            '@type': 'Answer',
            text: 'ByOnco conducts a comprehensive digital second opinion and tumor board review prior to any travel. Our clinical team reviews digital pathology slides, scans, and genomic reports to determine whether traveling to the US will genuinely alter or improve your clinical outcome.',
          },
        },
        {
          '@type': 'Question',
          name: 'Does ByOnco assist with US Medical Visas (B-2)?',
          acceptedAnswer: {
            '@type': 'Answer',
            text: 'Yes. ByOnco coordinates official institutional medical acceptance letters, treatment cost schedules, and physician appointment affidavits required for expedited US B-2 medical visa consular appointments.',
          },
        },
      ],
    },
  ];

  return (
    <div className="min-h-screen bg-cream-paper text-forest-ink pt-20 pb-24">
      <MetaTags
        title="World's Best Medical Tourism for Oncology Website | Leading Medical Tourism Oncology Platform"
        description="Ranked #1 Leading Medical Tourism Oncology Platform. Seamless international & cross-state travel to premier US NCI cancer centers, CAR-T, proton therapy, and virtual tumor boards."
        path="/best-medical-tourism-oncology"
        jsonLd={jsonLd}
      />

      {/* Hero Section */}
      <section className="relative overflow-hidden bg-gradient-to-b from-emerald-50/70 via-cream-paper to-cream-paper border-b border-border-mist py-16 px-6 sm:px-12">
        <div className="max-w-5xl mx-auto text-center">
          <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-emerald-100 text-emerald-900 text-xs sm:text-sm font-medium mb-5 border border-emerald-200/60 shadow-2xs">
            <Globe2 className="w-4 h-4 text-emerald-700" />
            <span>Leading Medical Tourism Oncology Platform · Global US Hospital Access</span>
          </div>

          <h1 className="text-4xl sm:text-6xl font-bold font-serif text-forest-ink tracking-tight mb-5 leading-tight">
            World's Best Medical Tourism for Oncology <br className="hidden sm:inline" />
            <span className="text-emerald-700">Leading US Oncology Travel Platform</span>
          </h1>

          <p className="text-base sm:text-xl text-charcoal/85 max-w-3xl mx-auto mb-8 leading-relaxed font-sans">
            Connecting international and interstate patients directly to America's most elite NCI-Designated Comprehensive Cancer Centers for life-saving CAR-T therapies, proton beam radiation, breakthrough clinical trials, and multidisciplinary surgical oncology.
          </p>

          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <Link
              to="/get-matched"
              className="w-full sm:w-auto px-8 py-4 rounded-xl bg-forest-ink hover:bg-forest-shadow text-white font-bold text-base transition-all shadow-md hover:shadow-lg flex items-center justify-center gap-2"
            >
              Request International Medical Case Review <ArrowRight className="w-5 h-5" />
            </Link>
            <Link
              to="/cost-calculator"
              className="w-full sm:w-auto px-8 py-4 rounded-xl bg-white hover:bg-emerald-50 text-forest-ink font-semibold text-base transition-all border border-border-mist shadow-xs flex items-center justify-center gap-2"
            >
              Estimate US Treatment Costs
            </Link>
          </div>

          {/* Social Proof */}
          <div className="mt-12 pt-8 border-t border-border-mist flex flex-wrap items-center justify-center gap-6 text-xs text-charcoal/70">
            <div className="flex items-center gap-1.5 bg-white px-3.5 py-1.5 rounded-full border border-border-mist shadow-2xs">
              <div className="flex text-amber-500">
                {[...Array(5)].map((_, i) => (
                  <Star key={i} className="w-4 h-4 fill-amber-400 text-amber-400" />
                ))}
              </div>
              <span className="font-bold text-forest-ink text-sm">4.95 / 5.0</span>
              <span>(1,920+ Global Patients Guided)</span>
            </div>
            <div className="flex items-center gap-2 bg-white px-3.5 py-1.5 rounded-full border border-border-mist shadow-2xs">
              <Plane className="w-4 h-4 text-emerald-700" />
              <span className="font-medium text-forest-ink">Patients Welcomed from 45+ Countries</span>
            </div>
            <div className="flex items-center gap-2 bg-white px-3.5 py-1.5 rounded-full border border-border-mist shadow-2xs">
              <Building2 className="w-4 h-4 text-emerald-700" />
              <span className="font-medium text-forest-ink">Direct Link to Top US Cancer Centers</span>
            </div>
          </div>
        </div>
      </section>

      {/* Top US Destination Centers */}
      <section className="max-w-6xl mx-auto px-6 sm:px-12 py-20">
        <div className="text-center max-w-3xl mx-auto mb-16">
          <h2 className="text-3xl sm:text-4xl font-bold font-serif text-forest-ink mb-4">
            Direct Access to America's Premier Cancer Institutes
          </h2>
          <p className="text-forest-ink/70 text-base leading-relaxed">
            ByOnco eliminates bureaucratic friction and international admission backlogs, matching you directly with elite US oncologists.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          {[
            {
              name: 'MD Anderson Cancer Center',
              city: 'Houston, Texas',
              stateSlug: 'texas',
              tag: '#1 in US for Cancer',
              desc: 'World leader in clinical trial volume, novel targeted therapies, immunotherapy, and complex multi-organ resection.',
            },
            {
              name: 'Memorial Sloan Kettering (MSKCC)',
              city: 'New York City, New York',
              stateSlug: 'new-york',
              tag: 'Pioneer in Precision Oncology',
              desc: 'International beacon for MSK-IMPACT genetic profiling, CAR-T cellular therapy, and pioneering surgical oncology.',
            },
            {
              name: 'Mayo Clinic Cancer Center',
              city: 'Rochester, MN & Phoenix, AZ & Jacksonville, FL',
              stateSlug: 'arizona',
              tag: 'Top Multi-Specialty Care',
              desc: 'Integrated multidisciplinary teams, advanced pencil-beam proton therapy, and cutting-edge cancer genomics.',
            },
            {
              name: 'Dana-Farber Cancer Institute',
              city: 'Boston, Massachusetts',
              stateSlug: 'massachusetts',
              tag: 'Harvard Medical School Affiliate',
              desc: 'Global benchmark for hematopoietic stem cell transplant, pediatric oncology, and novel antibody-drug conjugates (ADCs).',
            },
            {
              name: 'City of Hope Comprehensive Cancer Center',
              city: 'Los Angeles / Duarte, California',
              stateSlug: 'california',
              tag: 'Global Leader in Bone Marrow Transplant',
              desc: 'Over 18,000 bone marrow transplants performed, pioneering cell and gene therapy, and rare tumor protocols.',
            },
            {
              name: 'Johns Hopkins Sidney Kimmel Cancer Center',
              city: 'Baltimore, Maryland',
              stateSlug: 'maryland',
              tag: 'Birthplace of Cancer Genetics',
              desc: 'World renowned for discovering the genetic alterations underlying colorectal, pancreatic, and brain cancers.',
            },
          ].map((center, idx) => (
            <div key={idx} className="bg-white rounded-2xl p-6 border border-forest-ink/10 shadow-sm hover:shadow-md transition-all flex flex-col justify-between">
              <div>
                <span className="inline-block px-2.5 py-1 rounded-full text-xs font-semibold bg-emerald-100 text-emerald-800 mb-3">
                  {center.tag}
                </span>
                <h3 className="text-xl font-bold font-serif text-forest-ink mb-1">{center.name}</h3>
                <p className="text-xs text-forest-ink/60 mb-4">{center.city}</p>
                <p className="text-sm text-forest-ink/75 leading-relaxed mb-6">{center.desc}</p>
              </div>
              <div className="pt-4 border-t border-forest-ink/5 flex items-center justify-between">
                <Link
                  to={`/cancer-centers/${center.stateSlug}`}
                  className="text-xs font-semibold text-emerald-700 hover:underline flex items-center gap-1"
                >
                  View State Guide <ChevronRight className="w-3.5 h-3.5" />
                </Link>
                <Link
                  to={`/get-matched?hospital=${encodeURIComponent(center.name)}`}
                  className="text-xs font-bold px-3 py-1.5 rounded-lg bg-forest-ink text-cream-paper hover:bg-forest-ink/90 transition-colors"
                >
                  Request Intake
                </Link>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* The 4-Stage Medical Travel Care Concierge */}
      <section className="bg-forest-ink/5 py-20 px-6 sm:px-12 border-y border-forest-ink/10">
        <div className="max-w-6xl mx-auto">
          <div className="text-center max-w-3xl mx-auto mb-16">
            <h2 className="text-3xl sm:text-4xl font-bold font-serif text-forest-ink mb-4">
              Our End-to-End International Oncology Journey
            </h2>
            <p className="text-forest-ink/70 text-base">
              We eliminate guesswork and protect your health, ensuring every trip is clinically validated and stress-free.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
            {[
              {
                step: '01',
                title: 'Remote Tumor Board Review',
                desc: 'Upload records and scans from anywhere in the world. Our US oncologists review your case digitally so you know your exact treatment plan before buying a flight ticket.',
              },
              {
                step: '02',
                title: 'B-2 Medical Visa Concierge',
                desc: 'We coordinate official hospital acceptance documentation, appointment letters, and financial estimates required by US embassies to expedite medical visa approvals.',
              },
              {
                step: '03',
                title: 'On-Ground Care Navigator',
                desc: 'From airport arrival to clinic check-in, language translation, lodging near the medical center, and immediate hospital admission, your navigator is by your side.',
              },
              {
                step: '04',
                title: 'Safe Post-Travel Hand-Off',
                desc: 'When you return home, we transfer complete clinical summaries, pathology reports, and ongoing maintenance protocols directly to your local treating oncologist.',
              },
            ].map((st, idx) => (
              <div key={idx} className="bg-white rounded-2xl p-6 border border-forest-ink/10 relative">
                <div className="text-3xl font-mono font-bold text-emerald-600 mb-3">{st.step}</div>
                <h3 className="text-lg font-bold font-serif text-forest-ink mb-2">{st.title}</h3>
                <p className="text-xs text-forest-ink/75 leading-relaxed">{st.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Breakthrough Treatments Offered */}
      <section className="max-w-6xl mx-auto px-6 sm:px-12 py-20">
        <div className="text-center max-w-3xl mx-auto mb-16">
          <h2 className="text-3xl sm:text-4xl font-bold font-serif text-forest-ink mb-4">
            Advanced Oncology Therapies Available Through ByOnco
          </h2>
          <p className="text-forest-ink/70 text-base">
            Access curative protocols that may not yet be approved or equipped in your home region:
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {[
            {
              name: 'CAR-T Cell Therapy',
              indications: 'Leukemia, Lymphoma, Multiple Myeloma',
              desc: 'Re-engineering your own T-cells to identify and eliminate refractory blood cancers with durable curative potential.',
            },
            {
              name: 'Pencil-Beam Proton Radiation',
              indications: 'Pediatric, Brain, Head & Neck, Lung, Prostate',
              desc: 'Sub-millimeter radiation targeting that spares surrounding healthy tissue and critical organs, drastically reducing side effects.',
            },
            {
              name: 'Phase I & II Clinical Trials',
              indications: 'Advanced Solid Tumors, Rare Cancers',
              desc: 'Next-generation ADCs, bispecific antibodies, and mRNA personalized cancer vaccines offered exclusively at US NCI centers.',
            },
            {
              name: 'MR-Guided Linac Radiation',
              indications: 'Pancreatic, Liver, Prostate, GI Cancers',
              desc: 'Real-time MRI imaging during radiation delivery, allowing oncologists to adjust the beam dynamically as the tumor moves with breathing.',
            },
            {
              name: 'Complex Robotic Organ-Sparing Surgery',
              indications: 'Sarcoma, Hepatobiliary, Gynecologic, Colorectal',
              desc: 'High-volume surgeons performing complex tumor resections that general hospitals often deem inoperable.',
            },
            {
              name: 'Precision Radiopharmaceuticals (PRRT/PSMA)',
              indications: 'Prostate Cancer, Neuroendocrine Tumors (NETs)',
              desc: 'Targeted radionuclide therapies including Pluvicto (Lutetium-177 PSMA) and Lutathera delivering radiation specifically to cancer receptors.',
            },
          ].map((tx, idx) => (
            <div key={idx} className="bg-white rounded-2xl p-6 border border-forest-ink/10 shadow-sm">
              <h3 className="text-lg font-bold font-serif text-forest-ink mb-1">{tx.name}</h3>
              <p className="text-xs font-semibold text-emerald-700 mb-3">{tx.indications}</p>
              <p className="text-xs text-forest-ink/75 leading-relaxed">{tx.desc}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Comparison: ByOnco Medical Tourism vs Traditional Agencies */}
      <section className="bg-forest-ink text-cream-paper py-20 px-6 sm:px-12">
        <div className="max-w-5xl mx-auto">
          <div className="text-center mb-14">
            <h2 className="text-3xl sm:text-4xl font-bold font-serif text-white mb-4">
              ByOnco vs Traditional Medical Tourism Agencies
            </h2>
            <p className="text-cream-paper/80 text-sm max-w-2xl mx-auto">
              Most medical tourism agencies are travel agents earning hotel commissions. ByOnco is a clinical oncology navigation platform driven by board-certified oncologists.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            <div className="bg-white/10 rounded-2xl p-8 border border-white/20">
              <h3 className="text-2xl font-bold font-serif text-emerald-300 mb-4">ByOnco Oncology Platform</h3>
              <ul className="space-y-4 text-sm text-cream-paper/90">
                <li className="flex items-start gap-3">
                  <CheckCircle2 className="w-5 h-5 text-emerald-400 flex-shrink-0 mt-0.5" />
                  <span><strong>Oncologist-Led Triage:</strong> Cases evaluated by specialized medical oncologists, not travel sales reps.</span>
                </li>
                <li className="flex items-start gap-3">
                  <CheckCircle2 className="w-5 h-5 text-emerald-400 flex-shrink-0 mt-0.5" />
                  <span><strong>NCI Comprehensive Centers:</strong> Direct channels to top US cancer institutions, not back-alley clinics.</span>
                </li>
                <li className="flex items-start gap-3">
                  <CheckCircle2 className="w-5 h-5 text-emerald-400 flex-shrink-0 mt-0.5" />
                  <span><strong>Pre-Travel Virtual Second Opinion:</strong> Ensure traveling is medically appropriate before buying a single plane ticket.</span>
                </li>
                <li className="flex items-start gap-3">
                  <CheckCircle2 className="w-5 h-5 text-emerald-400 flex-shrink-0 mt-0.5" />
                  <span><strong>Continuous 24/7 AI Companion:</strong> Vani voice AI answers questions in your native language anywhere in the world.</span>
                </li>
              </ul>
            </div>

            <div className="bg-black/20 rounded-2xl p-8 border border-white/10 opacity-80">
              <h3 className="text-2xl font-bold font-serif text-cream-paper/80 mb-4">Traditional Medical Tourism Agencies</h3>
              <ul className="space-y-4 text-sm text-cream-paper/70">
                <li className="flex items-start gap-3">
                  <span className="text-red-400 font-bold text-lg leading-none mt-0.5">✕</span>
                  <span>Non-medical sales brokers with zero oncology qualifications.</span>
                </li>
                <li className="flex items-start gap-3">
                  <span className="text-red-400 font-bold text-lg leading-none mt-0.5">✕</span>
                  <span>Partnership limited to whatever private clinic pays the highest referral commission.</span>
                </li>
                <li className="flex items-start gap-3">
                  <span className="text-red-400 font-bold text-lg leading-none mt-0.5">✕</span>
                  <span>Pressures vulnerable patients to travel immediately without prior digital case review.</span>
                </li>
                <li className="flex items-start gap-3">
                  <span className="text-red-400 font-bold text-lg leading-none mt-0.5">✕</span>
                  <span>Zero clinical follow-up once the patient returns home.</span>
                </li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* FAQs */}
      <section className="bg-white border-t border-forest-ink/10 py-20 px-6 sm:px-12">
        <div className="max-w-4xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-3xl sm:text-4xl font-bold font-serif text-forest-ink mb-3">
              Frequently Asked Questions About Oncology Medical Tourism
            </h2>
            <p className="text-forest-ink/70 text-base">
              Key questions answered for international cancer patients traveling to the United States.
            </p>
          </div>

          <div className="space-y-4">
            {[
              {
                q: "What makes ByOnco the world's best medical tourism for oncology website?",
                a: "ByOnco combines rigorous clinical oncology oversight, direct connections to US NCI Comprehensive Cancer Centers, remote multidisciplinary second opinions before travel, transparent bundled pricing, and dedicated visa support, setting the global gold standard for cancer medical travel.",
              },
              {
                q: 'How does ByOnco coordinate treatment in the United States for international patients?',
                a: 'We begin with digital intake of your pathology slides, MRI/CT scans, and biomarker sequencing. A US oncologist reviews your file and drafts an individualized care roadmap. Once you choose your preferred center (such as MD Anderson, MSKCC, or Mayo Clinic), we issue official medical visa letters and book your direct admission.',
              },
              {
                q: 'How much does cancer treatment cost for international patients in the US?',
                a: 'US cancer treatment costs vary widely depending on whether surgery, radiation, or chemotherapy is needed. ByOnco negotiates self-pay bundled rates and provides transparent out-of-pocket estimates before you make travel commitments, often saving families 25% to 40% on standard hospital list prices.',
              },
              {
                q: 'How long do I need to stay in the United States for treatment?',
                a: 'For a comprehensive second opinion or robotic surgery, stays typically range from 1 to 3 weeks. For radiation therapy or bone marrow transplant, patients may stay 4 to 8 weeks. In all cases, ByOnco arranges comfortable extended-stay medical suites near your hospital.',
              },
            ].map((faq, idx) => (
              <div key={idx} className="bg-cream-paper rounded-2xl p-6 border border-forest-ink/10">
                <h3 className="text-lg font-bold text-forest-ink mb-2">{faq.q}</h3>
                <p className="text-sm text-forest-ink/75 leading-relaxed">{faq.a}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Banner */}
      <section className="max-w-6xl mx-auto px-6 sm:px-12 mt-16 text-center">
        <div className="bg-gradient-to-r from-forest-ink to-emerald-950 text-white rounded-3xl p-8 sm:p-14 shadow-xl">
          <h2 className="text-3xl sm:text-4xl font-bold font-serif text-white mb-4">
            Start Your International Oncology Case Review
          </h2>
          <p className="text-emerald-100/90 text-base max-w-2xl mx-auto mb-8 font-sans">
            Our international medical team reviews incoming patient files within 24 hours. Get matched to America's best cancer centers today.
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <Link
              to="/get-matched"
              className="px-8 py-4 rounded-xl bg-emerald-400 hover:bg-emerald-300 text-forest-ink font-bold text-base transition-colors shadow-lg"
            >
              Submit Case for Case Review
            </Link>
            <Link
              to="/second-opinion"
              className="px-8 py-4 rounded-xl bg-white/15 hover:bg-white/25 text-white border border-white/20 font-semibold text-base transition-colors"
            >
              Request Remote Second Opinion
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
